/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */

import {findCurrentAccountMatch} from '../entities/Accounts';
import {error} from '../logger/logger';
import {NotificationsAccount} from '../workers/types';
import {entityId} from './ConnectUtils';

/**
 * Provides a utility which derives the minimal NotificationsWorker payload
 * from the globalGroup state: the selected global group id, and the ids of
 * every account within the selected group. The account hierarchy itself is
 * never returned.
 *
 * ```typescript
 *
 *    import {toNotificationsAccount} from '@ion/core/src/services/FetchWorkerUtils';
 *    ...
 *
 *    // notify the worker of a global group change ...
 *    const {globalGroup} = getState();
 *    NotificationsWorker.onAccountChange(toNotificationsAccount(globalGroup));
 *
 * ```
 *
 * @param state   The current globalGroup state, or an object containing a
 *                globalGroup, globalGroupId, and accountHierarchy.
 * @returns       The `globalGroupId` and `accountIds` of the selected group,
 *                with `accountIds` set to `[]` when the ids can not be resolved.
 */
export const toNotificationsAccount = (state): NotificationsAccount => {
  const {globalGroup, globalGroupId, accountHierarchy} = state || {};
  let accountIds: number[] = [];
  if (globalGroup && accountHierarchy) {
    try {
      accountIds = getAllAccountIds(state, globalGroupId);
    } catch (err) {
      error(err);
    }
  }
  return {globalGroupId, accountIds};
};

/**
 * Provides a utility which returns the ids of all accounts within a global
 * group hierarchy.
 *
 * @param state The current globalGroup state, or an object containing a
 *        globalGroup, globalGroupId, and accountHierarchy.
 * @param accountId The parent account id (optional)
 * @param includeParent When true, the parent account id (if provided) or the
 *        state.globalGroupId will be included within the list of ids returned.
 */
export const getAllAccountIds = (state, parrentAccountId?: number, includeParent: boolean = true): number[] => {
  const accountIds = [];
  const {globalGroup, globalGroupId, accountHierarchy} = state;
  const [match] = findCurrentAccountMatch(accountHierarchy, globalGroup);
  if (match?.subAccounts) {
    const subAccounts = getSubAccount(match, []);
    accountIds.push(...subAccounts);
  }
  if (includeParent) {
    const id = parrentAccountId || globalGroupId;
    accountIds?.unshift(id);
  }
  return accountIds;
};

/**
 * Provides a utility which returns a list of ids within the entire account
 * hierarchy provided.
 *
 * @param parentAccount The account hierarchy from which all account ids are
 *        to be returned.
 * @param accountIds An initial array from which the ids are to be recursively
 *        added.
 */
export function getSubAccount(parentAccount, accountIds) {
  parentAccount.subAccounts?.forEach(sub => {
    const id = entityId(sub?.subaccount?.href || sub?.href);
    accountIds.push(id);
    const subAccounts = sub?.subaccount?.subAccounts || sub?.subAccounts;
    if (subAccounts) {
      getSubAccount(sub.subaccount || sub, accountIds);
    }
  });
  return accountIds;
};
